import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  RoutingCondition,
  RoutingQuestion,
} from "@/features/products/ui/onboarding-routing-field";

// Answer → round-robin routing rule. Identical to the onboarding RoutingRule
// except the target is a round-robin pool EventType (event_type_id) instead of a
// bundle. List order = priority (first match wins).
export interface RoundRobinRule {
  form_id?: string; // optional — scope to one form; empty = any form
  match: "all" | "any";
  conditions: RoutingCondition[];
  event_type_id: string;
}

const OPS = ["eq", "in", "gt", "lt", "contains", "exists"] as const;

const OP_LABEL: Record<string, string> = {
  eq: "is",
  in: "is one of",
  contains: "contains",
  gt: ">",
  lt: "<",
  exists: "is answered",
};

function opsForQuestion(q?: RoutingQuestion): { value: string; label: string }[] {
  const mk = (ops: string[]) =>
    ops.map((o) => ({ value: o, label: OP_LABEL[o] ?? o }));
  const t = q?.type;
  const hasOptions = !!q && q.options.length > 0;
  if (t === "yes_no") return mk(["eq"]);
  if (t === "number") return mk(["eq", "gt", "lt"]);
  if (t === "email" || t === "phone") return mk(["eq", "contains", "exists"]);
  if (t === "short_text" || t === "long_text" || t === "text")
    return mk(["contains", "eq", "exists"]);
  if (hasOptions) return mk(["eq", "in"]);
  return mk([...OPS]);
}

// Builder for answer → round-robin (EventType) routing rules. Mirrors
// OnboardingRoutingField; the only difference is the target picker.
export default function RoundRobinRoutingField({
  rules,
  onChange,
  forms,
  eventTypes,
  questionsByForm,
  lockedFormId,
}: {
  rules: RoundRobinRule[];
  onChange: (next: RoundRobinRule[]) => void;
  forms: { id: string; name: string }[];
  // The managed event's round-robin pool event types (id + a display label).
  eventTypes: { id: string; label: string }[];
  questionsByForm: Record<string, RoutingQuestion[]>;
  lockedFormId?: string;
}) {
  const patchRule = (i: number, patch: Partial<RoundRobinRule>) =>
    onChange(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const addRule = () =>
    onChange([
      ...rules,
      {
        match: "all",
        conditions: [{ field: "", op: "eq", value: "" }],
        event_type_id: "",
        ...(lockedFormId ? { form_id: lockedFormId } : {}),
      },
    ]);
  const removeRule = (i: number) => onChange(rules.filter((_, idx) => idx !== i));

  const patchCond = (ri: number, ci: number, patch: Partial<RoutingCondition>) =>
    patchRule(ri, {
      conditions: rules[ri].conditions.map((c, idx) =>
        idx === ci ? { ...c, ...patch } : c,
      ),
    });
  const addCond = (ri: number) =>
    patchRule(ri, {
      conditions: [...rules[ri].conditions, { field: "", op: "eq", value: "" }],
    });
  const removeCond = (ri: number, ci: number) =>
    patchRule(ri, {
      conditions: rules[ri].conditions.filter((_, idx) => idx !== ci),
    });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Routing rules{" "}
          <span className="text-gray-400">(top to bottom — first match wins)</span>
        </p>
        <Button type="button" size="sm" variant="outline" onClick={addRule}>
          <Plus className="h-4 w-4 mr-1" /> Add rule
        </Button>
      </div>

      {rules.length === 0 ? (
        <p className="text-xs text-gray-400">
          No rules — the default round-robin is always used.
        </p>
      ) : (
        rules.map((rule, ri) => (
          <div
            key={ri}
            className="rounded-md border border-gray-200 p-3 space-y-3 bg-white"
          >
            <div className="flex items-center justify-between">
              <Badge variant="outline">Rule {ri + 1}</Badge>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => removeRule(ri)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>

            <div className={lockedFormId ? "" : "grid grid-cols-2 gap-2"}>
              {!lockedFormId && (
                <div>
                  <p className="text-[11px] text-gray-500 mb-1">
                    Form (optional)
                  </p>
                  <Select
                    value={rule.form_id || "ANY"}
                    onValueChange={(v) =>
                      patchRule(ri, { form_id: v === "ANY" ? undefined : v })
                    }
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Any form" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ANY">Any form</SelectItem>
                      {forms.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <p className="text-[11px] text-gray-500 mb-1">Match</p>
                <Select
                  value={rule.match}
                  onValueChange={(v) =>
                    patchRule(ri, { match: v as "all" | "any" })
                  }
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All conditions</SelectItem>
                    <SelectItem value="any">Any condition</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[11px] text-gray-500">Conditions</p>
              {rule.conditions.map((c, ci) => {
                const effectiveFormId = rule.form_id ?? lockedFormId;
                const formQuestions = effectiveFormId
                  ? (questionsByForm[effectiveFormId] ?? [])
                  : [];
                const selectedQ = formQuestions.find((q) => q.id === c.field);
                const isYesNo = selectedQ?.type === "yes_no";
                const isNumber = selectedQ?.type === "number";
                const showOptionSelect =
                  !isYesNo &&
                  !!selectedQ &&
                  selectedQ.options.length > 0 &&
                  c.op !== "in" &&
                  c.op !== "exists";
                const opChoices = opsForQuestion(selectedQ);
                const opList = opChoices.some((o) => o.value === c.op)
                  ? opChoices
                  : [{ value: c.op, label: OP_LABEL[c.op] ?? c.op }, ...opChoices];
                const labelCls =
                  "text-[10px] uppercase tracking-wide text-gray-400 mb-1";
                return (
                  <div
                    key={ci}
                    className="rounded-md border border-gray-100 bg-gray-50/60 p-2.5 space-y-2"
                  >
                    <div className="min-w-0">
                      <p className={labelCls}>Question</p>
                      {formQuestions.length > 0 ? (
                        <Select
                          value={c.field || "NONE"}
                          onValueChange={(v) => {
                            const nextQ =
                              v === "NONE"
                                ? undefined
                                : formQuestions.find((q) => q.id === v);
                            patchCond(ri, ci, {
                              field: v === "NONE" ? "" : v,
                              value: "",
                              op: opsForQuestion(nextQ)[0]?.value ?? "eq",
                            });
                          }}
                        >
                          <SelectTrigger className="h-8 text-sm w-full min-w-0 [&>span]:truncate">
                            <SelectValue placeholder="Select question" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NONE">
                              Select question…
                            </SelectItem>
                            {formQuestions.map((q) => (
                              <SelectItem key={q.id} value={q.id}>
                                {q.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          className="h-8 text-sm w-full"
                          placeholder="answer field"
                          value={c.field}
                          onChange={(e) =>
                            patchCond(ri, ci, { field: e.target.value })
                          }
                        />
                      )}
                    </div>

                    <div className="flex items-end gap-2">
                      <div className="w-28 shrink-0">
                        <p className={labelCls}>Operator</p>
                        <Select
                          value={c.op}
                          onValueChange={(v) => patchCond(ri, ci, { op: v })}
                        >
                          <SelectTrigger className="h-8 text-sm w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {opList.map((op) => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={labelCls}>Answer</p>
                        {c.op === "exists" ? (
                          <Input
                            className="h-8 text-sm w-full"
                            disabled
                            placeholder="— not needed —"
                            value=""
                          />
                        ) : isYesNo ? (
                          <Select
                            value={c.value || "NONE"}
                            onValueChange={(v) =>
                              patchCond(ri, ci, {
                                value: v === "NONE" ? "" : v,
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-sm w-full">
                              <SelectValue placeholder="Select Yes / No" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NONE">Select…</SelectItem>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : showOptionSelect ? (
                          <Select
                            value={c.value || "NONE"}
                            onValueChange={(v) =>
                              patchCond(ri, ci, {
                                value: v === "NONE" ? "" : v,
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-sm w-full min-w-0 [&>span]:truncate">
                              <SelectValue placeholder="Select answer" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NONE">
                                Select answer…
                              </SelectItem>
                              {selectedQ!.options.map((o) => (
                                <SelectItem key={o} value={o}>
                                  {o}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : isNumber ? (
                          <Input
                            type="number"
                            className="h-8 text-sm w-full"
                            placeholder="value"
                            value={c.value}
                            onChange={(e) =>
                              patchCond(ri, ci, { value: e.target.value })
                            }
                          />
                        ) : (
                          <Input
                            className="h-8 text-sm w-full"
                            placeholder={c.op === "in" ? "a, b, c" : "value"}
                            value={c.value}
                            onChange={(e) =>
                              patchCond(ri, ci, { value: e.target.value })
                            }
                          />
                        )}
                      </div>

                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="shrink-0"
                        onClick={() => removeCond(ri, ci)}
                      >
                        <Trash2 className="h-4 w-4 text-gray-400" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => addCond(ri)}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add condition
              </Button>
            </div>

            <div>
              <p className="text-[11px] text-gray-500 mb-1">
                Direct to round-robin
              </p>
              <Select
                value={rule.event_type_id || "NONE"}
                onValueChange={(v) =>
                  patchRule(ri, { event_type_id: v === "NONE" ? "" : v })
                }
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select round-robin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">Select round-robin…</SelectItem>
                  {eventTypes.map((et) => (
                    <SelectItem key={et.id} value={et.id}>
                      {et.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
